import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ErrorPopup from "../../components/elements/ErrorPopup";
import Loader from "../../components/elements/Loader";
import SnackbarAlert from "../../components/elements/SnackbarAlert";
import useLandingAuthApi from "../../services/api/useLandingAuthApi";
import ROUTE_PATHS from "../../utils/routePaths";
import "../ListOfTripRequest/css/TripCard.css";
import "./css/UnregisteredAircraftTripsPage.css";
import PaginationComponent from "../ListOfTripRequest/views/PaginationComponent";
import UnregisteredAircraftTripsHeader from "./UnregisteredAircraftTripsHeader";
import UnregisteredAircraftTripCard from "./views/UnregisteredAircraftTripCard";
import ExportConfirmationModal from "../../components/elements/ExportConfirmationModal";

const UnregisteredAircraftTripsDetails = () => {
  const { fetchUnregisteredAircraftTripsByRegistration, exportUnregisteredAircraftTripsByRegistration } = useLandingAuthApi();
  const location = useLocation();
  const navigate = useNavigate();
  const listPage = parseInt(location?.state?.listPage) || 1;
  const initialPage = parseInt(location?.state?.detailsPage) || 1;
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportSuccessMessage, setExportSuccessMessage] = useState("");
  const itemsPerPage = 10;

  // Get aircraft registration number from localStorage instead of URL params
  const aircraftRegistrationNumber = localStorage.getItem(
    "unregisteredAircraftId"
  );
  const arrivalAirportId = localStorage.getItem(
    "unregisteredAircraftArrivalAirportId"
  );

  // Guard against invalid registration number
  const isValidRegistrationNumber =
    aircraftRegistrationNumber && aircraftRegistrationNumber.trim() !== "";

  const {
    data: tripsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "unregistered-aircraft-trips-details",
      aircraftRegistrationNumber,
      arrivalAirportId,
      currentPage,
      itemsPerPage,
    ],
    queryFn: async () => {
      if (!isValidRegistrationNumber) {
        throw new Error("Invalid aircraft registration number");
      }
      try {
        const result = await fetchUnregisteredAircraftTripsByRegistration(
          aircraftRegistrationNumber,
          arrivalAirportId,
          currentPage - 1,
          itemsPerPage
        );
        return result;
      } catch (error) {
        if (error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: isValidRegistrationNumber,
    keepPreviousData: true,
    retry: false,
  });

  let trips = [];
  if (tripsData?.data) {
    // Handle both array and object with content property
    if (Array.isArray(tripsData.data)) {
      trips = tripsData.data;
    } else if (tripsData.data.content) {
      trips = tripsData.data.content;
    } else if (tripsData.data.trips) {
      trips = tripsData.data.trips;
    }
  }

  const totalPages = tripsData?.data?.totalPages || 1;
  const totalElements = tripsData?.data?.totalElements || 0;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDownloadClick = () => {
    setIsExportModalOpen(true);
  };

  const exportTripsMutation = useMutation({
    mutationFn: () =>
      exportUnregisteredAircraftTripsByRegistration(
        aircraftRegistrationNumber,
        arrivalAirportId
      ),
    onSuccess: (response) => {
      // Show the message from response.data
      const message = response?.data;
      setExportSuccessMessage(message);
      setIsExportModalOpen(false);
      // Clear the message after 6 seconds (matching SnackbarAlert autoHideDuration)
      setTimeout(() => {
        setExportSuccessMessage("");
      }, 6000);
    },
    onError: (error) => {
      console.error("Export error:", error);
      setIsExportModalOpen(false);
    },
  });

  const handleExportConfirm = () => {
    if (!aircraftRegistrationNumber) {
      return;
    }
    exportTripsMutation.mutate();
  };

  const handleExportCancel = () => {
    setIsExportModalOpen(false);
  };

  if (!isValidRegistrationNumber) {
    localStorage.removeItem("unregisteredAircraftId");
    localStorage.removeItem("unregisteredAircraftArrivalAirportId");
    navigate(ROUTE_PATHS.UNREGISTERED_AIRCRAFT_TRIPS_PAGE, {
      state: { currentPage: listPage },
      replace: true,
    });
    return null;
  }

  return (
    <div>
      <UnregisteredAircraftTripsHeader
        currentPage={listPage}
        showBackLink={true}
      />
      {exportSuccessMessage && (
        <SnackbarAlert type="success">{exportSuccessMessage}</SnackbarAlert>
      )}
      {error && error.status !== 404 && (
        <div className="trip-request-error-message">
          <ErrorPopup children={error.message}></ErrorPopup>
        </div>
      )}
      {error && error.status === 404 && (
        <div className="no-trips-message-container">No trips available</div>
      )}
      {!error && (
        <>
          {isLoading && <Loader />}
          {trips.length === 0 && !isLoading && (
            <div className="no-trips-message-container">No trips available</div>
          )}
          {trips.length > 0 && !isLoading && (
            <>
              <div className="unregistered-aircraft-trips-details-controls">
                <button
                  className="unregistered-aircraft-trips-download-button"
                  onClick={handleDownloadClick}
                >
                  Download
                </button>
              </div>
              <div className="main-content">
                <div className="trip-request-list">
                  {trips.map((trip) => (
                    <UnregisteredAircraftTripCard
                      key={trip?.id}
                      tripData={trip}
                      listPage={listPage}
                      detailsPage={currentPage}
                    />
                  ))}
                </div>
              </div>
              {totalPages > 1 && (
                <PaginationComponent
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </>
      )}
      <ExportConfirmationModal
        isOpen={isExportModalOpen}
        onClose={handleExportCancel}
        onConfirm={handleExportConfirm}
        isLoading={exportTripsMutation.isPending}
      />
    </div>
  );
};

export default UnregisteredAircraftTripsDetails;
